/**
 * RCC Surface #4 — CIP Exception Queue.
 *
 * List + detail of CIP exceptions. Backed by /v3/qc/tasks filtered to
 * ExceptionCategory = CIP. FSR-first: FSR reviews, may escalate to Compliance.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { QcExceptionTaskDto } from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button, FilterChips, Pill } from '@/components/primitives';
import { formatLocalDate } from '@/utils/time';
import { SurfaceHeader } from '../components/SurfaceHeader';
import { ComplianceQueueRow } from '../components/ComplianceQueueRow';

type StatusFilter = 'all' | 'open' | 'completed';

export function CipExceptions(): JSX.Element {
  const apiClient = useApiClient();
  const { user } = useAuth();
  const [tasks, setTasks] = useState<QcExceptionTaskDto[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<StatusFilter>('open');
  const [selected, setSelected] = useState<QcExceptionTaskDto | null>(null);
  const [noteText, setNoteText] = useState<string>('');
  const abortRef = useRef<AbortController | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const filters: { officerEmail?: string; status?: number; top?: number } = { top: 200 };
      if (filter === 'open') filters.status = 2; // 2 = Assigned
      else if (filter === 'completed') filters.status = 5; // 5 = Completed
      const resp = await apiClient.retail.qc.listOpenTasks(filters, { signal: controller.signal });
      if (controller.signal.aborted) return;
      if (!resp.success) {
        setError(resp.error ?? 'Failed to list QC tasks');
        setTasks([]);
        return;
      }
      // Filter to CIP category client-side (QcExceptionCategory values TBD).
      const cipOnly = resp.tasks.filter((t) => t.exceptionCategoryName.toLowerCase().includes('cip'));
      setTasks(cipOnly);
    } catch (err) {
      if (controller.signal.aborted) return;
      setError(err instanceof Error ? err.message : 'Failed to load CIP exceptions');
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [apiClient, filter]);

  useEffect(() => {
    void refresh();
    return () => abortRef.current?.abort();
  }, [refresh]);

  const addNote = useCallback(
    async (task: QcExceptionTaskDto): Promise<void> => {
      const txt = noteText.trim();
      if (!txt) return;
      try {
        await apiClient.retail.qc.addNote({
          OfficerEmail: user?.upn ?? 'unknown@mypcb.com',
          AccountId: task.accountId,
          ExceptionTaskId: task.taskId,
          NoteText: txt,
        });
        void apiClient.retail.events
          .write({
            eventType: 'cip-exception-note-added',
            eventCategory: 'compliance',
            severity: 'info',
            actor: user?.upn ?? 'unknown',
            payloadJson: JSON.stringify({ taskId: task.taskId, accountId: task.accountId }),
          })
          .catch(() => undefined);
        setNoteText('');
        await refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to add note');
      }
    },
    [apiClient, noteText, refresh, user],
  );

  const complete = useCallback(
    async (task: QcExceptionTaskDto): Promise<void> => {
      try {
        await apiClient.retail.qc.completeTask(task.taskId, { officerEmail: user?.upn ?? 'unknown@mypcb.com' });
        void apiClient.retail.events
          .write({
            eventType: 'cip-exception-resolved',
            eventCategory: 'compliance',
            severity: 'info',
            actor: user?.upn ?? 'unknown',
            payloadJson: JSON.stringify({ taskId: task.taskId }),
          })
          .catch(() => undefined);
        setSelected(null);
        await refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to complete task');
      }
    },
    [apiClient, refresh, user],
  );

  return (
    <div>
      <SurfaceHeader
        title="CIP Exception Queue"
        scope="Customer Identification Program exceptions. FSR reviews; hard matches (true OFAC, ID fraud) escalate to Compliance."
        surfaceNumber={4}
        role="FSR"
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-4)', marginBottom: 'var(--sp-6)' }}>
        <FilterChips<StatusFilter>
          label="Status"
          selected={filter}
          onChange={setFilter}
          options={[
            { value: 'open', label: 'Open' },
            { value: 'completed', label: 'Completed' },
            { value: 'all', label: 'All' },
          ]}
        />
        <Button size="sm" variant="ghost" onClick={() => void refresh()}>
          Refresh
        </Button>
      </div>

      {error && (
        <div
          role="alert"
          style={{
            padding: 'var(--sp-5)',
            background: 'var(--err-bg)',
            color: 'var(--err-text)',
            borderRadius: 'var(--r-md)',
            marginBottom: 'var(--sp-5)',
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}

      {loading && <div style={{ color: 'var(--ink-600)', fontSize: 13 }}>Loading CIP exceptions…</div>}

      {!loading && tasks.length === 0 && (
        <div
          style={{
            padding: 'var(--sp-10)',
            textAlign: 'center',
            background: 'var(--ink-50)',
            border: '1px dashed var(--ink-300)',
            borderRadius: 'var(--r-md)',
            color: 'var(--ink-600)',
            fontSize: 13,
          }}
        >
          No CIP exceptions in this view.
        </div>
      )}

      {tasks.map((t) => (
        <ComplianceQueueRow
          key={t.taskId}
          title={t.taskName}
          subtitle={`Account ${t.accountId}${t.accountName ? ` · ${t.accountName}` : ''}`}
          meta={`${t.exceptionTypeName} · ${t.exceptionStatusName}`}
          severity={t.exceptionStatus === 2 ? 'warn' : 'ok'}
          slaBadge={t.dueDate ? { variant: 'muted', label: `Due ${formatLocalDate(t.dueDate)}` } : undefined}
          onClick={() => setSelected(t)}
        />
      ))}

      {selected && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            width: 420,
            background: 'var(--surface-card)',
            borderLeft: '1px solid var(--surface-border)',
            boxShadow: 'var(--sh-4)',
            padding: 'var(--sp-7)',
            overflowY: 'auto',
            zIndex: 20,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-4)', marginBottom: 'var(--sp-5)' }}>
            <h3 style={{ margin: 0 }}>Exception detail</h3>
            <Pill variant={selected.exceptionStatus === 2 ? 'warn' : 'ok'}>{selected.exceptionStatusName}</Pill>
          </div>
          <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 'var(--sp-3) var(--sp-5)', fontSize: 13 }}>
            <dt style={{ color: 'var(--ink-500)' }}>Task</dt>
            <dd style={{ margin: 0 }}>{selected.taskName}</dd>
            <dt style={{ color: 'var(--ink-500)' }}>Account</dt>
            <dd style={{ margin: 0 }}>{selected.accountId}</dd>
            <dt style={{ color: 'var(--ink-500)' }}>Category</dt>
            <dd style={{ margin: 0 }}>{selected.exceptionCategoryName}</dd>
            <dt style={{ color: 'var(--ink-500)' }}>Type</dt>
            <dd style={{ margin: 0 }}>{selected.exceptionTypeName}</dd>
            {selected.details && (
              <>
                <dt style={{ color: 'var(--ink-500)' }}>Details</dt>
                <dd style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{selected.details}</dd>
              </>
            )}
          </dl>
          <hr style={{ margin: 'var(--sp-6) 0', border: 'none', borderTop: '1px solid var(--surface-border)' }} />
          <label
            htmlFor="cip-note"
            style={{ display: 'block', fontSize: 11, textTransform: 'uppercase', color: 'var(--ink-500)', marginBottom: 'var(--sp-3)' }}
          >
            Add note
          </label>
          <textarea
            id="cip-note"
            rows={4}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            style={{
              width: '100%',
              padding: 'var(--sp-4)',
              border: '1px solid var(--ink-300)',
              borderRadius: 'var(--r-md)',
              fontFamily: 'var(--font-sans)',
              fontSize: 13,
            }}
          />
          <div style={{ display: 'flex', gap: 'var(--sp-3)', marginTop: 'var(--sp-4)' }}>
            <Button variant="ghost" onClick={() => setSelected(null)}>
              Close
            </Button>
            <Button variant="primary" onClick={() => void addNote(selected)} disabled={!noteText.trim()}>
              Add note
            </Button>
            <Button variant="green" onClick={() => void complete(selected)}>
              Mark complete
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
